import styles from 'css/src/components/embed-video.module.scss';
import 'css/src/global';

import React, { useEffect, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { useSelector } from 'react-redux';
import type { OpaqueVideoData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';

import { VideoStatus } from 'js/src/client';
import VideoPreview from 'js/src/components/common/video-preview/VideoPreview';
import { loadVideo } from 'js/src/data/share-video';
import { getAbsoluteUrl } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';
import {
    getLuminaryResolutionFromAvailScreenWidth,
    LuminaryResolutionPreset,
} from 'js/src/libs/video-data-consts';
import {
    LUMINARY_VIDEO_STORE_NAME,
    LuminaryContextProvider,
    luminaryReduxConnect,
} from 'js/src/luminary';
import { loadLuminaryAndWaitUntilReady } from 'js/src/luminary/can-play-utils';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

const LUMEN5_LOGO_URL =
    'https://cdn.lumen5.com/lumen5-site-images/L5-logo/2024/Lumen5-Brandmark-Reverse-White.png';

type EmbedVideoPropsFromRedux = ConnectedProps<typeof connector>;

const EmbedVideo = React.memo(function EmbedVideo({
    clearAndLoadLuminary,
}: EmbedVideoPropsFromRedux) {
    const dispatch = useAppDispatch();
    const video = useSelector((state: RootState) => state.shareVideo.video);
    const loading = useSelector((state: RootState) => state.shareVideo.loading);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const sharePageUrl = getAbsoluteUrl(video.get('share_link'));

    const handleWatchClick = React.useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.stopPropagation();
            if (e.metaKey || e.ctrlKey || e.shiftKey) {
                return;
            }
            e.preventDefault();
            const popup = window.open(sharePageUrl, '_blank');
            if (popup) {
                return;
            }
            if (!navigator.clipboard) {
                return;
            }
            navigator.clipboard.writeText(sharePageUrl).then(
                () => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                },
                () => {
                    // clipboard write rejected (e.g. sandboxed iframe without clipboard-write permission)
                },
            );
        },
        [sharePageUrl],
    );
    useEffect(() => {
        const doTheLoad = async () => {
            try {
                await dispatch(loadVideo());
            } catch (error) {
                setLoadingError('Error loading video');
            }
        };
        void doTheLoad();
    }, [dispatch]);

    useEffect(() => {
        if (!video.size) {
            return;
        }
        let cancelled = false;
        clearAndLoadLuminary(video.get('video_data').toJS()).catch((error) => {
            if (!cancelled) ErrorReporter.capture(error);
        });
        return () => {
            cancelled = true;
        };
    }, [clearAndLoadLuminary, video]);

    if (loading || !video.size) {
        return (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                <h4>Loading…</h4>
            </div>
        );
    }
    if (loadingError) {
        return (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                <h4>{loadingError}</h4>
            </div>
        );
    }

    const videoRenders = video.get('video_renders');
    const videoStatus = video.get('status');
    const sortedVideoSourceList =
        videoRenders.size > 1
            ? videoRenders.sortBy((segment) => segment.get('series_index'))
            : videoRenders;

    return (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center position-relative">
            <VideoPreview
                autoplay={false}
                startMuted={false}
                showMuteIndicator={true}
                enableKeyboardShortcuts={true}
                sortedVideoSourceList={sortedVideoSourceList}
                isHtmlVideo={videoStatus === VideoStatus.PUBLISHED}
            />
            <a
                className={styles['watch-on-lumen5']}
                href={sharePageUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWatchClick}
            >
                <img src={LUMEN5_LOGO_URL} alt="" aria-hidden="true" />
                {copied ? 'Link copied!' : 'Watch on Lumen5'}
            </a>
        </div>
    );
});

const mapLuminaryOperationsToProps = (
    _dispatch: AppDispatch,
    _getState: GetRootState,
    luminary: Luminary,
) => ({
    clearAndLoadLuminary: async (videoData: OpaqueVideoData) => {
        await luminary.clear();
        await loadLuminaryAndWaitUntilReady(luminary, videoData);
    },
});

const connector = luminaryReduxConnect(
    undefined,
    undefined,
    undefined,
    mapLuminaryOperationsToProps,
);
const EmbedVideoConnected = connector(EmbedVideo);

const EmbedVideoPage = React.memo(function EmbedVideoPage() {
    return (
        <LuminaryContextProvider
            minDimensionResolution={getLuminaryResolutionFromAvailScreenWidth(
                LuminaryResolutionPreset.HIGH,
            )}
            name={LUMINARY_VIDEO_STORE_NAME}
        >
            <EmbedVideoConnected />
        </LuminaryContextProvider>
    );
});

export default EmbedVideoPage;
